<template>
    <div class="table-container">
        <div class="p-0 pt-2 pb-1 header-panel-container">
            <mdb-navbar class="z-depth-1 p-0 pl-2 rounded-sm justify-content-between">
                <mdb-navbar-brand class="table-header-navbar-brand">
                    <mdb-tabs class="table-header-tabs" :active="0" default :links="[{ text: 'Purchase order lines' }]" />
                </mdb-navbar-brand>
                <mdb-form-inline class="ml-auto" tag="div">
                    <input
                        class="form-control mr-sm-2 table-searchterm-input border border-light"
                        :value="searchTerm"
                        @input="$emit('update:searchTerm', $event.target.value)"
                        type="text"
                        maxlength="100"
                        placeholder="PO No. / Item Code / Item Name"
                        aria-label="Search purchase order lines"
                        style="margin-top: 8px!important;"
                        @keydown.enter.prevent="$emit('search')"
                    />
                    <mdb-btn type="button" color="elegant" size="sm" :disabled="loading" @click="$emit('search')"><i class="el-icon-search"></i> Search</mdb-btn>
                    <mdb-btn type="button" color="elegant" class="ml-0" size="sm" :disabled="loading" @click="$emit('clear')"><i class="el-icon-close"></i> Clear</mdb-btn>
                    <mdb-btn type="button" color="elegant" class="ml-0" size="sm" :disabled="loading" @click="$emit('refresh')"><i class="el-icon-refresh"></i> Refresh</mdb-btn>
                </mdb-form-inline>
            </mdb-navbar>
        </div>
        <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon class="mb-2" />
        <el-table
            v-loading="loading"
            class="custom-element-table"
            :data="rows"
            :row-key="rowKey"
            border
            stripe
            size="small"
            height="calc(100vh - 140px)"
            :empty-text="error ? 'Unable to load purchase order lines' : 'No open purchase order lines found'"
        >
            <el-table-column prop="DocNum" label="PO NO." width="150" fixed>
                <template slot-scope="scope">
                    <el-button type="text" @click="$emit('row-click', scope.row)">{{ scope.row.DocNum || 'View purchase order' }}</el-button>
                </template>
            </el-table-column>
            <el-table-column prop="DocDate" label="PO DATE" width="140">
                <template slot-scope="scope">{{ formatManifestDate(scope.row.DocDate) || '-' }}</template>
            </el-table-column>
            <el-table-column prop="ETADate" label="ETA DATE" width="140">
                <template slot-scope="scope">{{ formatManifestDate(scope.row.ETADate) || '-' }}</template>
            </el-table-column>
            <el-table-column prop="ItemCode" label="ITEM CODE" width="190" />
            <el-table-column label="PHOTO" width="100" align="center">
                <template slot-scope="scope">
                    <img :src="scope.row.photo || defaultPhoto" alt="Item photo" class="line-photo" />
                </template>
            </el-table-column>
            <el-table-column prop="ItemName" label="ITEM NAME" min-width="260">
                <template slot-scope="scope">{{ scope.row.ItemName || '-' }}</template>
            </el-table-column>
            <el-table-column prop="Available" label="AVAILABLE" width="120" align="right">
                <template slot-scope="scope">{{ formatQty(scope.row.Available) }}</template>
            </el-table-column>
            <el-table-column prop="OpenQty" label="OPEN QTY" width="120" align="right">
                <template slot-scope="scope">{{ formatQty(scope.row.OpenQty) }}</template>
            </el-table-column>
            <el-table-column prop="ReservedInvoice" label="RESERVED INVOICE" min-width="190">
                <template slot-scope="scope">{{ reservedLabel(scope.row) }}</template>
            </el-table-column>
            <el-table-column prop="POStatus" label="PO STATUS" width="160">
                <template slot-scope="scope">{{ scope.row.POStatus || '-' }}</template>
            </el-table-column>
        </el-table>
        <el-pagination
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="page"
            :page-size="length"
            :page-sizes="[25, 50, 100]"
            :total="total"
            :disabled="loading"
            @current-change="$emit('page-change', $event)"
            @size-change="$emit('size-change', $event)"
        />
    </div>
</template>

<script>
import { mdbBtn, mdbFormInline, mdbNavbar, mdbNavbarBrand, mdbTabs } from 'mdbvue';
import { formatManifestDate } from '../manifest/formatters';

export default {
    name: 'OpenPurchaseOrderLinesTable',
    components: { mdbBtn, mdbFormInline, mdbNavbar, mdbNavbarBrand, mdbTabs },
    props: {
        rows: { type: Array, default: () => [] },
        searchTerm: { type: String, default: '' },
        page: { type: Number, default: 1 },
        length: { type: Number, default: 25 },
        total: { type: Number, default: 0 },
        loading: { type: Boolean, default: false },
        error: { type: String, default: '' }
    },
    data() {
        return {
            defaultPhoto: 'https://media.thatsliving.com/assets/default-item-img-small.jpg'
        };
    },
    methods: {
        formatManifestDate,

        /* A line is identified by its order and its position in it, not by either alone. */
        rowKey(row) {
            return row.DocEntry + '-' + row.LineNum;
        },

        /* SAP sends quantities as strings like "100.000000"; show them the way a person writes them. */
        formatQty(value) {
            if (value === null || value === undefined || value === '') return '-';
            const quantity = Number(value);
            if (!Number.isFinite(quantity)) return '-';
            return String(Number(quantity.toFixed(6)));
        },

        /* The invoice is the answer; the quantity is the qualifier, and it is absent whenever
           the reservation could not be converted to inventory units. */
        reservedLabel(row) {
            if (!row.ReservedInvoice) return '-';
            const reserved = this.formatQty(row.ReservedQty);
            return reserved === '-' ? row.ReservedInvoice : row.ReservedInvoice + ' (' + reserved + ')';
        }
    }
};
</script>

<style scoped>
.table-container{ padding : 0 5px } .custom-element-table{ position: relative; }
.line-photo{ width: 60px; height: 60px; object-fit: contain; }
</style>
